import { ErrorMessage, Field } from "formik";
import { Flag } from "lucide-react";
import TextError from "../../forms/TextError";
import AsyncPortSelect from "./AsyncPortSelect";
import AsyncTransporterSelect from "./AsyncTransporterSelect";
import PortAutoComplete from "./PortAutoComplete";
import SectionCard from "./SectionCard";
import FieldLabel from "./FieldLabel";
import { OCEAN_HAULAGE_MODE, OCEAN_SERVICE_TYPE } from "../../utils/constant";

const INPUT_CLS =
  "h-[42px] px-3.5 focus:outline-none block w-full text-sm border border-gray-200 rounded-xl text-gray-800 bg-white transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 hover:border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-100 disabled:cursor-not-allowed disabled:hover:border-gray-100";

// Mirrors OriginInfoForm — every field is gated by one of the three
// destination-side Rate scope toggles:
//  - Door Destination       → Door Destination, Delivery Service Type,
//                             Delivery Transporter, Delivery Haulage Mode
//  - Inland CY Destination  → Inland CY Destination (an ocean-port lookup,
//                             same as POD)
//  - CY Destination         → POD, Via Port Destination, Destination Depot
// POD / Inland CY Destination filter the preloaded `portList`
// (list_ocean_port) client-side; Door Destination / Via Port Destination
// are live lookups against ocean-manual-quote/get-locations (AsyncPortSelect).
const DestinationInfoForm = ({ formikData, portList, complete, readOnly }) => {
  const { values } = formikData;
  const scope = values.scope || {};
  const doorDestinationOn = !!scope.doorDestination;
  const inlandCyDestinationOn = !!scope.inlandCyDestination;
  const cyDestinationOn = !!scope.cyDestination;

  return (
    <SectionCard
      id="destination-section"
      icon={Flag}
      title="Destination information"
      subtitle="Discharge port, inland CY and last-mile delivery"
      complete={complete}
    >
      <div className="oq-field-grid">
        <div>
          <FieldLabel required={cyDestinationOn} hint="Port of Discharge — where the container comes off the vessel. Fixed to the query's own destination, not editable.">POD</FieldLabel>
          <PortAutoComplete
            name="destinationInfo.pod"
            value={values.destinationInfo?.pod}
            options={portList}
            setFieldValue={formikData.setFieldValue}
            placeholder={cyDestinationOn ? "POD" : "Enable 'CY Destination' in Rate scope"}
            disabled
          />
          <ErrorMessage name="destinationInfo.pod" component={TextError} />
        </div>

        <div>
          <FieldLabel required={inlandCyDestinationOn} hint="Container yard at an inland location the cargo moves to after POD. Fixed to the query's own destination, not editable.">
            Inland CY Destination
          </FieldLabel>
          <PortAutoComplete
            name="destinationInfo.inlandCyDestination"
            value={values.destinationInfo?.inlandCyDestination}
            options={portList}
            setFieldValue={formikData.setFieldValue}
            placeholder={inlandCyDestinationOn ? "Inland CY Destination" : "Enable 'Inland CY Destination' in Rate scope"}
            disabled
          />
          <ErrorMessage name="destinationInfo.inlandCyDestination" component={TextError} />
        </div>

        <div>
          <FieldLabel hint="An intermediate port the cargo transships through after POD, if any.">Via Port Destination</FieldLabel>
          <AsyncPortSelect
            name="destinationInfo.viaPortDestination"
            value={values.destinationInfo?.viaPortDestination}
            setFieldValue={formikData.setFieldValue}
            placeholder={cyDestinationOn ? "Via Port Destination" : "Enable 'CY Destination' in Rate scope"}
            disabled={readOnly || !cyDestinationOn}
            locationType="destination"
          />
        </div>

        <div>
          <FieldLabel>Destination Depot</FieldLabel>
          <Field
            type="text"
            name="destinationInfo.destinationDepot"
            placeholder={cyDestinationOn ? "Enter Destination Depot" : "Enable 'CY Destination' in Rate scope"}
            className={INPUT_CLS}
            disabled={readOnly || !cyDestinationOn}
          />
        </div>

        <div>
          <FieldLabel required={doorDestinationOn}>Door Destination</FieldLabel>
          <AsyncPortSelect
            name="destinationInfo.doorDestination"
            value={values.destinationInfo?.doorDestination}
            setFieldValue={formikData.setFieldValue}
            placeholder={doorDestinationOn ? "Door Destination" : "Enable 'Door Destination' in Rate scope"}
            disabled={readOnly || !doorDestinationOn}
            locationType="destination"
          />
          <ErrorMessage name="destinationInfo.doorDestination" component={TextError} />
        </div>

        <div>
          <FieldLabel>Delivery Service Type</FieldLabel>
          <Field
            as="select"
            name="destinationInfo.deliveryServiceType"
            className={INPUT_CLS}
            disabled={readOnly || !doorDestinationOn}
          >
            <option value="">{doorDestinationOn ? "Select Service Type" : "Enable 'Door Destination' in Rate scope"}</option>
            {OCEAN_SERVICE_TYPE.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Field>
        </div>

        <div>
          <FieldLabel>Delivery Transporter</FieldLabel>
          <AsyncTransporterSelect
            name="destinationInfo.deliveryTransporter"
            value={values.destinationInfo?.deliveryTransporter}
            setFieldValue={formikData.setFieldValue}
            placeholder={doorDestinationOn ? "Delivery Transporter" : "Enable 'Door Destination' in Rate scope"}
            disabled={readOnly || !doorDestinationOn}
          />
        </div>

        <div>
          <FieldLabel hint="The mode of transport moving cargo between the port and the door — road, rail or barge.">
            Delivery Haulage Mode
          </FieldLabel>
          <Field
            as="select"
            name="destinationInfo.deliveryHaulageMode"
            className={INPUT_CLS}
            disabled={readOnly || !doorDestinationOn}
          >
            <option value="">{doorDestinationOn ? "Select Delivery Haulage" : "Enable 'Door Destination' in Rate scope"}</option>
            {OCEAN_HAULAGE_MODE.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Field>
        </div>
      </div>
    </SectionCard>
  );
};

export default DestinationInfoForm;
